import { i18n } from '../i18n.js';
import { MoonfallAnalytics } from '../analytics/posthog-init.js';
import { warnBeforeEnabling } from './duel-cost-warning.js';
import { answerLockedTap, markSwitchLocked } from './locked-switch.js';
let MangaGamePreference = {};


MangaGamePreference = (function () {
    function uiText(key, fallback) {
        const translated = i18n?.t?.(key);
        return translated && translated !== key ? translated : fallback;
    }

    /* EVERY BATTLE STARTS OFF (owner, 2026-08-15): "make the manga switch for
       paid players for every game default off ... they can turn it on. The
       normal players cannot, but it starts off all the time." This reverses the
       earlier default-on instruction.

       So there is deliberately NO persisted preference: a remembered choice is
       exactly what "starts off all the time" forbids. The switch is per-battle
       state that lives for one mount, and the control writes nothing to
       storage. `deck-selector.js` still sends the resulting boolean explicitly
       on every start, because the server reads an ABSENT `includeManga` as the
       pre-preference default (on) rather than as off. */

    /* Three states, one control:
         eligible + available  — a live switch
         eligible, exhausted   — inert, "your allowance resets next month"
         not eligible          — inert, and it says whose feature this is
       The third used to render NOTHING, so a free player's battle simply had
       no manga and no reason given. An absent control cannot answer a question
       the player is already asking. Inert in both dark states: it never
       toggles, never writes a preference, and always reports `false` upward —
       the server's entitlement check is untouched and stays authoritative.
       Inert is not mute, though: the thumb wears a padlock and a tap is
       answered with the reason (locked-switch.js), because on a phone the
       reason line is off screen and a dead tap read as a broken switch. */
    /* DEVELOPMENT-ONLY ENTITLEMENT PREVIEW.
       "can u remove manga sub from my account so i can see how screen looks
       without it?" — mount() already renders all three entitlement states, so
       the honest answer is to render one, not to mutate a real subscription.

       `?mangaPreview=none|exhausted|subscriber` overrides ONLY the status
       object this control renders, and sticks for the session so you can move
       around the app; `?mangaPreview=off` clears it. It is refused outright in
       production, it never writes a preference, and it warns on every mount so
       a preview can never be mistaken for a real entitlement. The server's
       check is untouched and remains authoritative — this changes a picture,
       not a permission. */
    const PREVIEW_KEY = 'moonfall:manga-preview';
    const PREVIEW_SHAPES = {
        none: { eligible: false, available: false, reason: 'subscription_required' },
        exhausted: { eligible: true, available: false, reason: 'allowance_exhausted' },
        subscriber: { eligible: true, available: true, reason: null },
    };

    /* Read the URL at LOAD, not at mount. The control only mounts when the deck
       picker opens, so `?mangaPreview=off` did nothing on a plain page load and
       the flag survived invisibly in sessionStorage — you could not turn the
       preview off without opening the very screen it affects. */
    function syncPreviewFlagFromUrl() {
        if (window.__MOONFALL_CONFIG?.isProd) return;
        try {
            const want = new URLSearchParams(window.location.search).get('mangaPreview');
            if (!want) return;
            if (want === 'off') window.sessionStorage.removeItem(PREVIEW_KEY);
            else if (PREVIEW_SHAPES[want]) window.sessionStorage.setItem(PREVIEW_KEY, want);
        } catch (_) { /* storage unavailable; the preview simply stays off */ }
    }

    function previewOverride(status) {
        if (window.__MOONFALL_CONFIG?.isProd) return status;
        let want = null;
        try {
            syncPreviewFlagFromUrl();
            want = window.sessionStorage.getItem(PREVIEW_KEY);
        } catch (_) {
            return status;
        }
        const shape = want && PREVIEW_SHAPES[want];
        if (!shape) return status;
        console.warn(
            `[MangaGamePreference] PREVIEW "${want}" — this is a client-side picture of an `
            + 'entitlement state. The account and the server check are unchanged. '
            + 'Clear it with ?mangaPreview=off',
        );
        return { ...status, ...shape };
    }

    function mount(slot, status = {}) {
        if (!slot) throw new Error('Manga preference slot is required');
        status = previewOverride(status);
        slot.replaceChildren();

        /* A status that never arrived is NOT a verdict.
           The loader returns `undefined` when the entitlement query fails, and
           an absent status used to fall through to the third state — so a
           transient failure told a PAYING subscriber that Manga Pass inks your
           duel into manga, i.e. sold them what they already own, and left the
           switch dark. Unknown gets its own copy and makes no claim either way;
           the server's entitlement check was and remains the authority. */
        const known = status !== null && status !== undefined
            && (status.eligible === true || status.eligible === false);
        const usable = status?.eligible === true && status?.available === true;
        /* A verdict, not a gap: no pass, or this cycle's allowance is spent. */
        const refused = known && !usable;
        let enabled = false;
        const root = document.createElement('div');
        root.className = 'manga-game-preference';
        if (!usable) root.classList.add('is-disabled');
        if (!known) root.classList.add('is-unknown');

        const description = document.createElement('span');
        description.id = 'manga-game-preference-description';
        description.className = 'manga-game-preference__description';
        if (usable) {
            description.textContent = uiText(
                'manga.preference.description',
                'Draws your duel as comic panels as you play.',
            );
        } else if (status?.eligible === true) {
            description.textContent = uiText(
                'manga.preference.monthlyExhausted',
                'Your manga allowance is used up. More at its next reset.',
            );
        } else if (!known) {
            description.textContent = uiText(
                'manga.preference.statusUnavailable',
                'Checking your Manga Pass…',
            );
        } else {
            description.textContent = uiText(
                'manga.preference.mangaPassRequired',
                'Turn your duels into comic panels. Included with Manga Pass.',
            );
        }

        const label = document.createElement('span');
        label.className = 'manga-game-preference__label';
        label.textContent = uiText('manga.preference.label', 'Manga panels');

        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'manga-game-preference__switch';
        toggle.setAttribute('role', 'switch');
        toggle.setAttribute('aria-describedby', description.id);
        toggle.setAttribute('aria-label', label.textContent);
        if (!usable) toggle.setAttribute('aria-disabled', 'true');

        const state = document.createElement('span');
        state.className = 'manga-game-preference__state';
        const compactLabel = document.createElement('span');
        compactLabel.className = 'manga-game-preference__compact-label';
        compactLabel.textContent = uiText('manga.preference.compactLabel', 'Manga');
        const track = document.createElement('span');
        track.className = 'manga-game-preference__track';
        track.setAttribute('aria-hidden', 'true');
        const thumb = document.createElement('span');
        thumb.className = 'manga-game-preference__thumb';
        track.appendChild(thumb);
        if (refused) markSwitchLocked(root, thumb);

        function render() {
            toggle.setAttribute('aria-checked', String(enabled));
            state.textContent = enabled
                ? uiText('manga.preference.on', 'On')
                : uiText('manga.preference.off', 'Off');
        }

        toggle.addEventListener('click', async () => {
            if (!usable) {
                /* Inert is not silent. The tap is acknowledged and answered
                   with the same sentence the description carries — on a phone
                   that sentence is otherwise never on screen. */
                answerLockedTap(root, toggle, { message: description.textContent, refused });
                MoonfallAnalytics?.capture?.('manga_game_preference_locked_tap', {
                    reason: refused
                        ? (status?.reason || (status?.eligible ? 'allowance_exhausted' : 'subscription_required'))
                        : 'status_unknown',
                });
                return;
            }
            /* Arming manga costs Moonlight exactly as arming Sharper Minds does,
               so it owes the player the same warning. */
            if (!enabled && !(await warnBeforeEnabling('manga'))) return;
            enabled = !enabled;
            render();
            MoonfallAnalytics?.capture?.('manga_game_preference_changed', { enabled });
        });
        toggle.append(compactLabel, state, track);
        root.append(label, toggle, description);
        slot.appendChild(root);
        render();

        return Object.freeze({
            value: () => (usable ? enabled : false),
            destroy: () => root.remove(),
        });
    }

    syncPreviewFlagFromUrl();

    return { mount };
})();

export { MangaGamePreference };
